import {
  ComponentProps,
  ChangeEvent,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

/**
 * @component
 * Компонент поля
 * @property Доступны все пропсы для типа Input
 * @property clearable - Добавляет кнопку для очистки поля
 * @property hasError - Добавляет красную границу при ошибке
 * @description
 * Текстовое поле с возможностью очистки
 */

const stringifyInputValue = (
  inputValue: ComponentProps<"input">["value"] | ComponentProps<"input">["defaultValue"]
) => {
  if (inputValue === undefined || inputValue === null) {
    return "";
  }

  return Array.isArray(inputValue) ? inputValue.join(",") : String(inputValue);
};

const CustomInput = forwardRef<
  HTMLInputElement,
  ComponentProps<"input"> & { clearable?: boolean; hasError?: boolean }
>(
  (
    {
      readOnly,
      className,
      clearable,
      value,
      defaultValue,
      onChange,
      hasError,
      ...props
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const isControlled = value !== undefined;
    const [internalValue, setInternalValue] = useState(() =>
      stringifyInputValue(isControlled ? value : defaultValue)
    );

    useEffect(() => {
      if (isControlled) {
        setInternalValue(stringifyInputValue(value));
      }
    }, [isControlled, value]);

    useEffect(() => {
      if (isControlled || !inputRef.current) {
        return;
      }

      const domValue = inputRef.current.value;
      setInternalValue((prev) => (prev === domValue ? prev : domValue));
    }, [isControlled, defaultValue]);

    const setCombinedRef = useCallback(
      (node: HTMLInputElement | null) => {
        inputRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref]
    );

    const dispatchNativeInputEvent = useCallback((nextValue: string) => {
      if (!inputRef.current) {
        return false;
      }

      const inputElement = inputRef.current;
      const valueSetter = Object.getOwnPropertyDescriptor(
        HTMLInputElement.prototype,
        "value"
      )?.set;

      if (!valueSetter) {
        return false;
      }

      valueSetter.call(inputElement, nextValue);
      inputElement.dispatchEvent(new Event("input", { bubbles: true }));
      return true;
    }, []);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      if (!isControlled) {
        setInternalValue(e.target.value);
      }
      onChange?.(e);
    };

    const handleClear = () => {
      if (!isControlled) {
        setInternalValue("");
      }

      const dispatched = dispatchNativeInputEvent("");
      if (!dispatched) {
        const event = {
          target: { value: "", name: props.name },
          currentTarget: { value: "", name: props.name },
        } as ChangeEvent<HTMLInputElement>;

        onChange?.(event);
      }
    };

    const borderColor = hasError
      ? "border-[#b21913] border-[2px]"
      : "border-[#B1B1B1]";
    const textStyle = !hasError ? "text-[#313131]" : "text-[#b21913]";
    const currentValue = isControlled ? stringifyInputValue(value) : internalValue;

    return (
      <div className="relative">
        <input
          {...props}
          defaultValue={defaultValue}
          ref={setCombinedRef}
          value={isControlled ? value : undefined}
          onChange={handleChange}
          className={`w-full placeholder:text-[#31313199] placeholder:font-medium ${textStyle} bg-white border-1 ${borderColor} rounded-[15px] px-[1.063rem] py-[0.938rem] outline-none disabled:bg-gray-300 ${
            readOnly && "cursor-default"
          } ${className}`}
        />
        {clearable && currentValue && !readOnly && !props.disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-[1.063rem] top-1/2 -translate-y-1/2 !p-0 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600"
            aria-label="Очистить поле"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </div>
    );
  }
);

CustomInput.displayName = "CustomInput";

export default CustomInput;
